import { getCurrentInstance } from 'vue';
import { logger, type LogContext, LogLevel } from '@/logger';

/**
 * Vue composable that provides logging methods with automatic component context enrichment.
 *
 * Automatically detects the current Vue component name and enriches all log entries with:
 * - Component name
 * - Current pathname
 * - Current route (pathname + search params)
 *
 * This eliminates the need to manually specify component information in every log call
 * and ensures consistent logging context across the application.
 *
 * @returns An object containing the component name and logging methods (debug, info, warn, error, critical)
 *
 * @example
 * ```typescript
 * // In a Vue component
 * import { useComponentLogger } from '@/logger';
 *
 * const logger = useComponentLogger();
 *
 * // Log a warning with additional context
 * logger.warn('View Article is missing required parameters', {
 *   url: props.endpoint.toString(),
 *   logCode: LogCode.MISSING_PARAMETERS,
 *   dataFields: {
 *     userIds: nlaUserIds,
 *     branding: props.branding,
 *     articleIds: props.ids || '[MISSING]',
 *     mmoId: props.mmoId,
 *   },
 * });
 *
 * // Log an error with exception
 * try {
 *   await riskyOperation();
 * } catch (error) {
 *   logger.error(
 *     'Operation failed',
 *     {
 *       url: props.endpoint.toString(),
 *       logCode: LogCode.MISSING_PARAMETERS,
 *       dataFields: {
 *         userIds: nlaUserIds,
 *         branding: props.branding,
 *         articleIds: props.ids || '[MISSING]',
 *         mmoId: props.mmoId,
 *       },
 *     },
 *     error
 *   );
 * }
 * ```
 */

export function useComponentLogger() {
  const instance = getCurrentInstance();
  const componentName = instance?.type.__name ?? instance?.type.name ?? 'UnknownComponent';

  /**
   * Internal helper that enriches log context with component information
   * @private
   */
  const logWithContext = (
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: Error
  ) => {
    const enrichedContext = {
      component: componentName,
      pathname: window.location.pathname,
      route: window.location.pathname + window.location.search,
      ...context,
    };

    logger[level](message, enrichedContext, error);
  };

  /**
   * Creates a logging method for the specified level
   * @private
   */
  const createLogMethod = (level: LogLevel) =>
    (message: string, context?: LogContext, error?: Error) =>
      logWithContext(level, message, context, error);

  return {
    /**
     * The name of the current Vue component (automatically detected)
     */
    componentName,

    /**
     * Logs a debug message with component context.
     * Use for detailed information only when diagnosing problems.
     *
     * @param message - The log message
     * @param context - Optional context object with additional data
     * @param error - Optional error object
     */
    debug: createLogMethod(LogLevel.DEBUG),

    /**
     * Logs an info message with component context.
     * Use for general information about application flow.
     *
     * @param message - The log message
     * @param context - Optional context object with additional data
     * @param error - Optional error object
     */
    info: createLogMethod(LogLevel.INFO),

    /**
     * Logs a warning message with component context.
     * Use for potentially harmful situations that don't stop execution.
     *
     * @param message - The log message
     * @param context - Optional context object with additional data
     * @param error - Optional error object
     */
    warn: createLogMethod(LogLevel.WARN),

    /**
     * Logs an error message with component context.
     * Use for error events that might still allow the application to continue running.
     *
     * @param message - The log message
     * @param context - Optional context object with additional data
     * @param error - Optional error object
     */
    error: createLogMethod(LogLevel.ERROR),

    /**
     * Logs a critical message with component context.
     * Use for very severe error events that will presumably lead the application to abort.
     *
     * @param message - The log message
     * @param context - Optional context object with additional data
     * @param error - Optional error object
     */
    critical: createLogMethod(LogLevel.CRITICAL),
  };
}
